import React, { useContext, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

import {
  Region,
  SCEmployee,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import mainStyles from "../../styles";
import RegionSelector from "./shared/region-selector";
import { bulkMoveGRS as bulkMoveGRSApi } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { getScUserEmail } from "helpers/get-sc-user-email";
import { getScUserName } from "helpers/get-sc-user-name";

interface BulkMoveGRSFormProps {
  listSupportWorkflows: () => void;
}

const BulkMoveGRSForm = ({ listSupportWorkflows }: BulkMoveGRSFormProps) => {
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [scUserName, setScUserName] = useState("");
  const [scUserEmail, setScUserEmail] = useState("");
  const [formState, setFormState] = useState<{
    sourceOrgId: string;
    targetOrgId: string;
    globalResponseSetIds: string;
    allGRS: boolean;
    Region: Region;
  }>({
    sourceOrgId: "",
    targetOrgId: "",
    globalResponseSetIds: "",
    allGRS: false,
    Region: Region.UNSPECIFIED,
  });

  const { showError } = useContext(SnackbarContext);
  const [targetRegion, setTargetRegion] = useState<Region>();

  useEffect(() => {
    getScUserName().then((name) => setScUserName(name));
    getScUserEmail().then((email) => setScUserEmail(email));
  }, []);

  const resetFormState = () => {
    setFormState({
      sourceOrgId: "",
      targetOrgId: "",
      globalResponseSetIds: "",
      allGRS: false,
      Region: Region.UNSPECIFIED,
    });
  };

  const bulkMoveGRS = async () => {
    try {
      setIsLoading(true);
      const globalResponseSetIds = formState.allGRS
        ? []
        : cleanAndSplitInput(formState.globalResponseSetIds);

      await bulkMoveGRSApi(
        {
          source_org_id: formState.sourceOrgId,
          target_org_id: formState.targetOrgId,
          global_response_set_ids: globalResponseSetIds,
          region: formState.Region,
          sc_employee: new SCEmployee({
            name: scUserName,
            email: scUserEmail,
          }),
        },
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );

      resetFormState();
      listSupportWorkflows();
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceOrgIdChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleTextAreaChange(event, "sourceOrgId");
  };

  const handleTargetOrgIdChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleTextAreaChange(event, "targetOrgId");
  };

  const handleGlobalResponseSetIdsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleTextAreaChange(event, "globalResponseSetIds");
  };

  const handleAllGRSToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      allGRS: event.target.checked,
    });
  };

  const CLEAN_INPUT_REGEX = /^(?=\n)$|^\s*|\s*$|\n\n+/gm;

  const cleanAndSplitInput = (input: string): string[] => {
    return input.replace(CLEAN_INPUT_REGEX, "").split(/\r?\n/).filter(Boolean);
  };

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
  ) => {
    const value = event.target.value;

    setFormState({
      ...formState,
      [key]: value,
    });
  };

  const BulkMoveGRSCard = () => {
    return (
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="info-panel-content"
            id="info-panel-header"
            data-testid="info-panel-summary"
          >
            <HelpIcon color="primary" />
            <Typography
              data-testid="info-panel-title"
              style={{ paddingLeft: ".25em" }}
            >
              How to use: Bulk Transfer GRS
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              style={{ textAlign: "left" }}
              data-testid="info-panel-description"
            >
              <b>Summary:</b>
              <p>
                Bulk transfer GRS will take an either all or a selected set of
                global response sets from an organisation and move them to
                another organisation. Please note that this will not copy the
                GRS, it will transfer them so they will no longer be in the
                source organisation. Also, the region must be the same for both
                orgs. Any templates in source org that have questions containing
                the GRS that is being moved will be broken. After the GRS has
                been moved to the target org, the GRS ID will stay the same, so
                there is no need to update the templates in the target org.
              </p>
              <b>Workflow Fields:</b>
              <ul>
                <li>
                  <b>Source Org ID</b> - Org ID to transfer GRS from.
                </li>
                <li>
                  <b>Target Org ID</b> - Org ID the transfer GRS to.
                </li>
                <li>
                  <b>Region</b> - The region in which the customers data is
                  stored.
                </li>
                <li>
                  <b>GRS to transfer</b> - List of GRS to transfer to another
                  org, one per row.
                </li>
                <li>
                  <b>Apply to All GRS</b> - When toggled on will apply the
                  transfer to all GRS in the org.
                </li>
              </ul>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="source-org-id-input"
            label="Source Org ID"
            value={formState.sourceOrgId}
            onChange={handleSourceOrgIdChange}
            variant="outlined"
            size="small"
            disabled={isLoading}
            inputProps={{ "data-testid": "source-org-id-input" }}
            style={{ minWidth: 400 }}
          />

          <TextField
            id="target-org-id-input"
            label="Target Org ID"
            value={formState.targetOrgId}
            onChange={handleTargetOrgIdChange}
            variant="outlined"
            size="small"
            disabled={isLoading}
            inputProps={{ "data-testid": "target-org-id-input" }}
            style={{ minWidth: 400 }}
          />
          <RegionSelector
            targetRegion={targetRegion}
            setTargetRegion={setTargetRegion}
            isLoading={isLoading}
          />
          <br />

          <Grid
            item
            xs={12}
            style={{
              marginTop: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TextField
              id="grs-ids-input"
              label="GRS to Transfer"
              multiline
              maxRows={10}
              value={formState.globalResponseSetIds}
              onChange={handleGlobalResponseSetIdsChange}
              variant="outlined"
              size="small"
              disabled={isLoading || formState.allGRS}
              inputProps={{ "data-testid": "grs-ids-input" }}
              style={{ minWidth: 400 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formState.allGRS}
                  onChange={handleAllGRSToggle}
                />
              }
              label="Transfer all GRS"
              labelPlacement="top"
              data-testid="apply-to-all-grs-checkbox"
            />
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                bulkMoveGRS();
              }}
              loading={isLoading}
              disabled={
                !formState.sourceOrgId ||
                !formState.targetOrgId ||
                targetRegion === undefined ||
                (!formState.globalResponseSetIds && !formState.allGRS)
              }
              data-testid="execute-workflow-button"
            >
              Execute Workflow
            </LoadingButton>
          </Grid>
        </Box>
      </Grid>

      <Grid item xs={12} style={{ paddingBottom: "20px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <BulkMoveGRSCard />
        </Grid>
      </Grid>
    </>
  );
};

export default BulkMoveGRSForm;
